import {
  Avatar,
  Box,
  FlexBox,
  Icon,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { useCallback, useRef } from 'react';

import { CustomFieldAttribute, CustomFieldEntity } from 'apis/customFields';
import {
  useOrderedDragDrop,
  UseOrderedDragDropOptions,
} from 'utils/useOrderedDragDrop';

import { customAttributeTypeAttributes } from '../../utils/customAttributeTypeAttributes';

import { ActiveToggle } from './components/actions/active/activeToggle';
import { EditAction } from './components/actions/edit/editAction';
import styles from './customAttributesTableRow.module.scss';

/** The draggable type. */
const type = Symbol('attribute');

type CustomAttributesTableRowProps = Pick<
  UseOrderedDragDropOptions,
  'onMove'
> & {
  index: number;
  attribute: CustomFieldAttribute;
  entityId: CustomFieldEntity;
  onActiveStateChange?: (attributeId: string) => void;
};

/** Represents the row item for a custom attributes table. */
export const CustomAttributesTableRow = ({
  attribute,
  index,
  entityId,
  onMove,
  onActiveStateChange,
}: CustomAttributesTableRowProps): JSX.Element => {
  const { is_deactivated, is_required } = attribute;
  const isRequired = is_required;
  const row = useRef<HTMLTableRowElement>(null);
  const rowHandle = useRef<HTMLDivElement>(null);
  const { isDragging, isOver } = useOrderedDragDrop({
    type,
    index,
    source: rowHandle,
    target: row,
    preview: row,
    onMove,
    triggerSwap: 'drop',
  });

  const rowCellClassNames = classnames({
    [styles.cellOver]: isOver,
  });

  const handleActiveStateChange = useCallback(
    () => onActiveStateChange?.(attribute.id),
    [attribute.id, onActiveStateChange],
  );

  return (
    <Table.Tr ref={row} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Table.Td className={rowCellClassNames}>
        <FlexBox gap="300" alignItems="center">
          {!attribute.is_deactivated && (
            <div ref={rowHandle} className={styles.handle}>
              <Icon name="bars" />
            </div>
          )}

          <Tooltip
            wrap="div"
            wrapProps={{ style: { display: 'flex' } }}
            content={`${
              customAttributeTypeAttributes[attribute.type].label
            } attribute`}
          >
            <Avatar
              color={
                is_deactivated
                  ? {
                      contentColor: colors.neutral[700],
                      backgroundColor: colors.neutral[100],
                    }
                  : undefined
              }
              icon={customAttributeTypeAttributes[attribute.type].icon}
            />
          </Tooltip>

          <Box>
            <Text.Paragraph variant="1" subdued={is_deactivated}>
              {attribute.label} {isRequired && '*'}
            </Text.Paragraph>

            {attribute.description && (
              <Text.Paragraph subdued variant="2">
                {attribute.description}
              </Text.Paragraph>
            )}
          </Box>
        </FlexBox>
      </Table.Td>

      <Table.Td className={rowCellClassNames}>
        <Text.Paragraph as="span" variant="4" subdued={is_deactivated}>
          {attribute.identifier}
        </Text.Paragraph>
      </Table.Td>

      <Table.Td className={rowCellClassNames}>
        <ActiveToggle
          attribute={attribute}
          onChange={handleActiveStateChange}
        />
      </Table.Td>

      <Table.Td className={rowCellClassNames}>
        <FlexBox alignItems="center" justifyContent="end" gap="100">
          <EditAction attribute={attribute} entityId={entityId} />
        </FlexBox>
      </Table.Td>
    </Table.Tr>
  );
};
